/// <reference path="index.ts" />
import produce from "immer";
import { update } from "lodash";
import moment, { Moment } from "moment";
import { useMemo } from "react";
import { DeepPartial } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { AnyObjectSchema, mixed, object, string } from "yup";
import { Pageable } from "../../../../services/pageable";
import { deepOmitBy } from "../../../../utils/utils";
import {
  AddressAttachmentType,
  countries,
  IdentityAttachmentType,
  useAddressAttachmentType,
  useIdentityAttachmentType,
} from "../../../common/models";
import { getFields, validationFields } from "../../../common/models/fields";
import { UsersDTO } from "../../../users/models/dto";
import { StatusEnum } from "../../../users/models/internal";
import {
  DeclineCode,
  FileCategory,
  FileType,
  Service,
  ServiceType,
  VerificationData,
} from "../internal";

export namespace VerificationDTO {
  export namespace Responses {
    export namespace GetKYBJurisdictions {
      export type T = Array<{
        code: string;
        name: string;
      }>;
    }
    export namespace GetMerchantConfig {
      export type T = {
        createComplianceTicket: true;
        includedServices: Array<typeof ServiceType.T>;
        excludedServices: [];
        outsourceComplianceReviews: boolean;
      };
    }

    export namespace GetSingleKyc {
      export interface T {
        consentNote?: string;
        createdDate: Moment;
        fraudCheckId: string;
        companyId: string;
        ongoingBackgroundChecks: boolean;
        serviceDetails: Array<Service>;
        user: {
          address?: {
            country?: typeof countries.T | null;
            city: string | null;
            state: string | null;
            streetAddress: string | null;
            zip?: string;
          };
          dateOfBirth: Moment | null;
          emailAddress: string;
          firstName: string;
          identityDocument?: {
            expiryDate: Moment | null;
            issueDate: Moment | null;
            number: string | null;
          } | null;
          locale: string | null;
          lastName: string | null;
          middleName: string | null;
          nationality: typeof countries.T | null;
        };
        userStatus: StatusEnum;
      }

      export const defaults: Partial<T> = {};

      export const parse: (body: any) => T = produce((body: any) => {
        update(body, "createdDate", x => (x ? moment.utc(x) : x));
        update(body, "user.dateOfBirth", x => (x ? moment.utc(x) : x));
        update(body, "user.identityDocument.expiryDate", x =>
          x ? moment.utc(x) : x
        );
        update(body, "user.identityDocument.issueDate", x =>
          x ? moment.utc(x) : x
        );

        body.serviceDetails.forEach((service: any) => {
          service.details.documents.forEach((x: any) => {
            x.uploadedAt = moment.utc(x.uploadedAt);
          });
        });

        return body;
      });
    }

    export namespace GetAllKyc {
      export interface T {
        content: GetSingleKyc.T[];
        pageable: Pageable.Response.T;
      }

      export const defaults: T = {
        content: [],
        pageable: Pageable.Response.defaults,
      };
    }

    export namespace GetSingleKyb {
      export interface T {
        fraudCheckId: string;
        amlResponse: {
          incorporationDate: Date | null;
          companyName: string | null;
          declineCodes: Array<DeclineCode> | null;
          filters: Array<string>;
        } | null;
        companyJurisdictionCountryCode: string | null;
        companyRegistrationNumber: string;
        createdDate: Date;
        email: string | null;
        status: StatusEnum;
        verificationData: VerificationData;
      }
    }

    export namespace GetAllKyb {
      export interface T {
        content: GetSingleKyb.T[];
        pageable: Pageable.Response.T;
      }

      export const defaults: T = {
        content: [],
        pageable: Pageable.Response.defaults,
      };
    }

    export namespace UploadIndividualKyc {
      export interface T {
        fraudCheckId: string;
        status: StatusEnum;
      }
    }

    export namespace UploadBusinessKyb {
      export type T = UploadIndividualKyc.T;
    }

    export namespace GetChecksSummary {
      interface Entry {
        totalFromBeginningOfTime: number;
        maxPerWeek: number;
        totalPerWeek: { date: string; count: number }[];
      }

      export interface T {
        kycChecks?: Entry;
        kybChecks?: Entry;
      }
    }

    export namespace KycHistory {
      export type T = Array<{
        changedAt: Moment;
        changedBy: string;
        note: string;
        mainStatus: StatusEnum;
        documentStatus: StatusEnum;
        addressStatus: StatusEnum;
        faceStatus: StatusEnum;
        consentStatus: StatusEnum;
        amlStatus: StatusEnum;
      }>;

      export const parse = produce(state => {
        if (Array.isArray(state)) {
          state.forEach(entry => {
            entry.changedAt = moment(entry.changedAt);
          });
        } else {
          throw new Error("Passed argument shape is incorrect");
        }
      });
    }
  }

  export namespace Requests {
    export namespace UpdateKycStatuses {
      export interface T {
        note?: string;
        serviceDeclineCodes: Record<string, Array<typeof DeclineCode.T>>;
      }

      export const useFields: () => AnyObjectSchema = () => {
        const { t } = useTranslation("fields");

        return useMemo<AnyObjectSchema>(
          () =>
            object().shape({
              note: string().required().label(t("note")),
              serviceDeclineCodes: mixed(),
            }),
          [t]
        );
      };
    }

    export namespace UpdateKycStatus {
      export interface T {
        note?: string;
        status: StatusEnum;
      }

      export const useFields: () => AnyObjectSchema = () => {
        const { t } = useTranslation("fields");

        return useMemo<AnyObjectSchema>(
          () =>
            object().shape({
              note: string().required().label(t("note")),
              status: mixed()
                .oneOf(StatusEnum.keys)
                .required()
                .label(t("status")),
            }),
          [t]
        );
      };
    }

    export namespace UploadKycDocument {
      export interface T {
        file: File | null;
        category: FileCategory["T"];
        type: FileType["T"];
      }
    }

    export namespace UploadIndividualKyc {
      export interface T {
        userDetails: {
          firstName: string;
          middleName?: string;
          lastName: string;
          emailAddress?: string;
          dateOfBirth: Date;
          nationality: typeof countries.T;
          address: {
            streetAddress?: string;
            city: string;
            zip: string;
            state?: string;
            country: typeof countries.T;
          };
          locale: string;
        };
        backgroundChecks?: { ongoingCheck: boolean };
        note?: string;
        ticketDetails?: {
          emailAddress: string;
        };
        fraudCheckId: string;
        identityDetails: {
          attachment: {
            file: File;
            type: ReturnType<typeof useIdentityAttachmentType>["T"];
          };
          additionalFile?: File;
          identity: {
            number: string;
            issueDate?: string;
            expiryDate?: string;
          };
        };
        addressAttachment: {
          file: File;
          type: ReturnType<typeof useAddressAttachmentType>["T"];
        };
        faceAttachment?: File;
        wallet?: {
          address: string;
          chain: string;
        };
      }

      export const useFields = () => {
        const { t } = useTranslation("fields");
        const emailSchemaFn = validationFields.email.useSchema();
        const emailSchema = useMemo(() => emailSchemaFn.notRequired(), [
          emailSchemaFn,
        ]);

        const fraudCheckIdSchema = validationFields.fraudCheckId.useSchema();

        const dateOfBirthSchema = validationFields.dateOfBirth.useSchema();

        const firstNameSchema = validationFields.firstName.useSchema();

        const middleNameSchema = validationFields.middleName.useSchema();

        const lastNameSchema = validationFields.lastName.useSchema();

        const nationalitySchema = validationFields.nationality.useSchema();
        const citySchema = validationFields.city.useSchema();
        const zipSchema = validationFields.zip.useSchema();
        const stateSchema = validationFields.state.useSchema();
        const countrySchema = validationFields.country.useSchema();
        const localeSchema = validationFields.language.useSchema();

        const backgroundChecksSchema = validationFields.backgroundChecks.useSchema();
        const noteSchema = validationFields.note.useSchema();
        const fraudCheckId = {
          name: validationFields.fraudCheckId.useName(),
          schema: fraudCheckIdSchema,
        };
        const backgroundChecks = {
          name: validationFields.backgroundChecks.useName(),
          schema: backgroundChecksSchema,
        };
        const note = {
          name: validationFields.note.useName(),
          schema: noteSchema,
        };

        const streetAddressSchemaFn = validationFields.streetAddress.useSchema();
        const streetAddressSchema = useMemo(
          () => streetAddressSchemaFn.required(),
          [streetAddressSchemaFn]
        );

        const identityDocumentNumberSchemaFn = validationFields.identityDocumentNumber.useSchema();
        const identityDocumentNumberSchema = useMemo(
          () => identityDocumentNumberSchemaFn.required(),
          [identityDocumentNumberSchemaFn]
        );

        const identityDocumentIssueDateSchemaFn = validationFields.identityDocumentIssueDate.useSchema();
        const identityDocumentIssueDateSchema = useMemo(
          () => identityDocumentIssueDateSchemaFn.required(),
          [identityDocumentIssueDateSchemaFn]
        );

        const identityDocumentExpiryDateSchema = validationFields.identityDocumentExpiryDate.useSchema();

        const userDetails = {
          name: useMemo(() => t("user-details"), [t]),
          schema: useMemo(
            () =>
              object()
                .shape({
                  firstName: firstNameSchema,
                  middleName: middleNameSchema,
                  lastName: lastNameSchema,
                  emailAddress: emailSchema,
                  dateOfBirth: dateOfBirthSchema,
                  nationality: nationalitySchema,
                  address: object()
                    .shape({
                      streetAddress: streetAddressSchema,
                      city: citySchema,
                      zip: zipSchema,
                      state: stateSchema,
                      country: countrySchema,
                    })
                    .required()
                    .label(t("address")),
                  locale: localeSchema,
                })
                .required()
                .label(t("user-details")),
            [
              firstNameSchema,
              middleNameSchema,
              lastNameSchema,
              emailSchema,
              dateOfBirthSchema,
              nationalitySchema,
              streetAddressSchema,
              citySchema,
              zipSchema,
              stateSchema,
              countrySchema,
              localeSchema,
              t,
            ]
          ),
        };

        const ticketDetails = {
          name: useMemo(() => t("ticket-details"), [t]),
          schema: useMemo(
            () =>
              object()
                .shape({
                  emailAddress: emailSchema,
                })
                .label(t("ticket-details")),
            [t, emailSchema]
          ),
        };

        const IdentityFileType = useIdentityAttachmentType();

        const identityDetails = {
          name: useMemo(() => t("identity-details"), [t]),
          schema: useMemo(
            () =>
              object()
                .shape({
                  attachment: object().shape({
                    file: mixed()
                      .test(
                        "is-large",
                        t(`file-size-cannot-exceed-6-mb`),
                        (file: any) =>
                          !(file instanceof File) ||
                          file.size <= 6 * 1024 * 1024
                      )
                      .required()
                      .label(t("identity-file")),
                    type: mixed()
                      .oneOf(IdentityFileType.keys)
                      .label(t("identity-file-type"))
                      .required(),
                  }),
                  additionalFile: mixed()
                    .test(
                      "is-large",
                      t(`file-size-cannot-exceed-6-mb`),
                      (file: any) =>
                        !(file instanceof File) || file.size <= 6 * 1024 * 1024
                    )
                    .label(t("additional-file")),
                  identity: object().shape({
                    number: identityDocumentNumberSchema,
                    expiryDate: identityDocumentExpiryDateSchema,
                    issueDate: identityDocumentIssueDateSchema,
                  }),
                })
                .label(t("identity-details")),
            [
              t,
              IdentityFileType,
              identityDocumentNumberSchema,
              identityDocumentExpiryDateSchema,
              identityDocumentIssueDateSchema,
            ]
          ),
        };

        const AddressFileType = useAddressAttachmentType();

        const addressAttachment = {
          name: useMemo(() => t("address-attachment"), [t]),
          schema: useMemo(
            () =>
              object().shape({
                file: mixed()
                  .test(
                    "is-large",
                    t(`file-size-cannot-exceed-6-mb`),
                    (file: any) =>
                      !(file instanceof File) || file.size <= 6 * 1024 * 1024
                  )
                  .required()
                  .label(t("address-file")),
                type: mixed()
                  .oneOf(AddressFileType.keys)
                  .label(t("address-file-type"))
                  .required(),
              }),
            [t, AddressFileType]
          ),
        };

        const faceAttachment = {
          name: useMemo(() => t("face-attachment"), [t]),
          schema: useMemo(
            () =>
              mixed()
                .test(
                  "is-large",
                  t(`file-size-cannot-exceed-6-mb`),
                  (file: any) =>
                    !(file instanceof File) || file.size <= 6 * 1024 * 1024
                )
                .required()
                .label(t("face-attachment")),
            [t]
          ),
        };

        const chainSchema = validationFields.chain.useSchema();
        const addressSchema = validationFields.address.useSchema();
        const walletSchema = useMemo(
          () =>
            object().shape({
              chain: chainSchema,
              address: addressSchema,
            }),
          [chainSchema, addressSchema]
        );

        const wallet = {
          name: useMemo(() => t("wallet"), [t]),
          schema: walletSchema,
        };

        return useMemo(
          () =>
            getFields({
              userDetails,
              backgroundChecks,
              note,
              ticketDetails,
              fraudCheckId,
              identityDetails,
              addressAttachment,
              faceAttachment,
              wallet,
            }),
          [
            userDetails.name,
            userDetails.schema,
            backgroundChecks.name,
            backgroundChecks.schema,
            note.name,
            note.schema,
            ticketDetails.name,
            ticketDetails.schema,
            fraudCheckId.name,
            fraudCheckId.schema,
            identityDetails.name,
            identityDetails.schema,
            addressAttachment.name,
            addressAttachment.schema,
            faceAttachment.name,
            faceAttachment.schema,
            wallet.name,
            wallet.schema,
          ]
        );
      };

      export const defaults: DeepPartial<T> = {
        userDetails: {
          locale: "en",
        },
      };

      export const mapFromUser: (
        userDetails: UsersDTO.Responses.GetSingle.T
      ) => DeepPartial<T> = userDetails => {
        const address = {
          streetAddress: userDetails.streetAddress,
          city: userDetails.city,
          zip: userDetails.zip,
          state: userDetails.state,
          country: userDetails.country,
        };
        return {
          userDetails: { ...userDetails, address },
        } as DeepPartial<T>;
      };
    }

    export namespace UpdateIndividualKYC {
      export interface T {
        userDetails: {
          firstName: string;
          middleName?: string;
          lastName: string;
          emailAddress: string;
          dateOfBirth: Date;
          nationality: typeof countries.T;
          address: {
            streetAddress?: string;
            city: string;
            zip: string;
            state?: string;
            country: typeof countries.T;
          };
          locale: string;
        };
        identity: {
          number: string;
          issueDate?: string;
          expiryDate?: string;
        };
      }

      export const useFields = () => {
        const { t } = useTranslation("fields");
        const emailSchemaFn = validationFields.email.useSchema();
        const emailSchema = useMemo(() => emailSchemaFn.notRequired(), [
          emailSchemaFn,
        ]);

        const dateOfBirthSchema = validationFields.dateOfBirth.useSchema();

        const firstNameSchema = validationFields.firstName.useSchema();

        const middleNameSchema = validationFields.middleName.useSchema();

        const lastNameSchema = validationFields.lastName.useSchema();

        const nationalitySchema = validationFields.nationality.useSchema();
        const citySchema = validationFields.city.useSchema();
        const zipSchema = validationFields.zip.useSchema();
        const stateSchema = validationFields.state.useSchema();
        const countrySchema = validationFields.country.useSchema();
        const localeSchema = validationFields.language.useSchema();

        const streetAddressSchemaFn = validationFields.streetAddress.useSchema();
        const streetAddressSchema = useMemo(
          () => streetAddressSchemaFn.required(),
          [streetAddressSchemaFn]
        );

        const identityDocumentNumberSchemaFn = validationFields.identityDocumentNumber.useSchema();
        const identityDocumentNumberSchema = useMemo(
          () => identityDocumentNumberSchemaFn.required(),
          [identityDocumentNumberSchemaFn]
        );

        const identityDocumentIssueDateSchemaFn = validationFields.identityDocumentIssueDate.useSchema();
        const identityDocumentIssueDateSchema = useMemo(
          () => identityDocumentIssueDateSchemaFn.required(),
          [identityDocumentIssueDateSchemaFn]
        );

        const identityDocumentExpiryDateSchema = validationFields.identityDocumentExpiryDate.useSchema();

        const userDetails = {
          name: useMemo(() => t("user-details"), [t]),
          schema: useMemo(
            () =>
              object()
                .shape({
                  firstName: firstNameSchema,
                  middleName: middleNameSchema,
                  lastName: lastNameSchema,
                  emailAddress: emailSchema,
                  dateOfBirth: dateOfBirthSchema,
                  nationality: nationalitySchema,
                  address: object()
                    .shape({
                      streetAddress: streetAddressSchema,
                      city: citySchema,
                      zip: zipSchema,
                      state: stateSchema,
                      country: countrySchema,
                    })
                    .required()
                    .label(t("address")),
                  locale: localeSchema,
                })
                .required()
                .label(t("user-details")),
            [
              firstNameSchema,
              middleNameSchema,
              lastNameSchema,
              emailSchema,
              dateOfBirthSchema,
              nationalitySchema,
              streetAddressSchema,
              citySchema,
              zipSchema,
              stateSchema,
              countrySchema,
              localeSchema,
              t,
            ]
          ),
        };

        const identity = {
          name: useMemo(() => t("identity-details"), [t]),
          schema: useMemo(
            () =>
              object()
                .shape({
                  number: identityDocumentNumberSchema,
                  expiryDate: identityDocumentExpiryDateSchema,
                  issueDate: identityDocumentIssueDateSchema,
                })
                .label(t("identity-details")),
            [
              t,
              identityDocumentNumberSchema,
              identityDocumentExpiryDateSchema,
              identityDocumentIssueDateSchema,
            ]
          ),
        };

        return useMemo(
          () =>
            getFields({
              userDetails,
              identity,
            }),
          [userDetails.name, userDetails.schema, identity.name, identity.schema]
        );
      };

      export const defaults: DeepPartial<T> = {
        userDetails: {
          locale: "en",
        },
      };

      export const mapFromSingleKyc: (
        item: Responses.GetSingleKyc.T
      ) => DeepPartial<T> = item => {
        return deepOmitBy(
          {
            userDetails: {
              firstName: item.user.firstName,
              middleName: item.user.middleName,
              lastName: item.user.lastName,
              emailAddress: item.user.emailAddress,
              dateOfBirth: item.user.dateOfBirth,
              nationality: item.user.nationality,
              address: item.user.address,
              locale: item.user.locale,
            },
            identity: item.user.identityDocument,
          },
          x => x === undefined || x === null
        ) as DeepPartial<T>;
      };
    }

    export namespace UpdateDocumentType {
      export interface T {
        type: IdentityAttachmentType | AddressAttachmentType;
      }

      export const useFields = () => {
        const { t } = useTranslation("fields");

        const identityAttachmentType = useIdentityAttachmentType();
        const addressAttachmentType = useAddressAttachmentType();

        return useMemo(
          () =>
            getFields({
              type: {
                name: "type",
                schema: mixed()
                  .oneOf([
                    ...identityAttachmentType.keys,
                    ...addressAttachmentType.keys,
                  ])
                  .label(t("type")),
              },
            }),
          [t, identityAttachmentType, addressAttachmentType]
        );
      };

      export const defaults: DeepPartial<T> = {};
    }

    export namespace UploadSelfKyc {
      export type T = UploadIndividualKyc.T;

      export const useFields = UploadIndividualKyc.useFields;
      export const defaults: Partial<T> = {};
    }

    export namespace UploadBusinessKyb {
      export interface T {
        fraudCheckId: string;
        companyRegistrationNumber: string;
        companyJurisdictionCountryCode: string;
        businessAml?: {
          ongoingCheck: boolean;
          callbackUrl?: string;
        };
        companyName: string;
      }
      export const useFields = () => {
        const fraudCheckIdSchema = validationFields.fraudCheckId.useSchema();
        const companyRegistrationNumberSchema = validationFields.companyRegistrationNumber.useSchema();
        const companyJurisdictionCountryCodeSchema = validationFields.companyJurisdictionCountryCode.useSchema();
        const businessAmlSchema = validationFields.businessAml.useSchema();
        const companyNameSchema = validationFields.companyName.useSchema();

        const fraudCheckId = {
          name: validationFields.fraudCheckId.useName(),
          schema: fraudCheckIdSchema,
        };
        const companyRegistrationNumber = {
          name: validationFields.companyRegistrationNumber.useName(),
          schema: companyRegistrationNumberSchema,
        };
        const companyJurisdictionCountryCode = {
          name: validationFields.companyJurisdictionCountryCode.useName(),
          schema: companyJurisdictionCountryCodeSchema,
        };
        const businessAml = {
          name: validationFields.businessAml.useName(),
          schema: businessAmlSchema,
        };
        const companyName = {
          name: validationFields.companyName.useName(),
          schema: companyNameSchema,
        };

        return useMemo(
          () =>
            getFields({
              fraudCheckId,
              companyRegistrationNumber,
              companyJurisdictionCountryCode,
              businessAml,
              companyName,
            }),
          [
            fraudCheckId.name,
            fraudCheckId.schema,
            companyRegistrationNumber.name,
            companyRegistrationNumber.schema,
            companyJurisdictionCountryCode.name,
            companyJurisdictionCountryCode.schema,
            businessAml.name,
            businessAml.schema,
            companyName.name,
            companyName.schema,
          ]
        );
      };

      export const defaults: T = {
        fraudCheckId: "",
        companyRegistrationNumber: "",
        companyJurisdictionCountryCode: "",
        companyName: "",
      };
    }
  }
}
